import 'bootstrap/dist/css/bootstrap.css';
import 'bootstrap-vue-next/dist/bootstrap-vue-next.css';
// Design System - CSS Variables & Utilities (NEW)
import './assets/css/design-system.css';
// Gamification / Tik – variables globales (Espace Tik, Store, Wallet, Ligues)
import '@/domains/gamification/styles/espace-tik.css';

// Navbar Design System - Centralized navbar styles
import './assets/css/navbar-system.css';
// Compiled CSS from SCSS
import './assets/css/dized.css';
import './assets/css/bootstrap-brand-bridge.css';
import './assets/css/custom.css';
import '@/domains/soccer-v2/styles/soccer-v2-theme.css';
import '@/domains/mode-fennec/styles/fennec-theme.css';
import '@/domains/mode-fennec/styles/fennec-widgets.css';
import './assets/css/customizer.css';
import './assets/css/rtl.css';
import './assets/css/dark-theme.css';
import '@/domains/mode-fennec/styles/fennec-tokens.css';
import '@/domains/mode-fennec/styles/fennec-chat.css';
import '@/domains/mode-fennec/styles/fennec-feed.css';
import '@/domains/mode-fennec/styles/fennec-soccer.css';
import '@/domains/mode-fennec/styles/fennec-stories-gami.css';
import '@/domains/mode-fennec/styles/fennec-shell.css';
import 'zuck.js/dist/zuck.min.css'
import { createApp, nextTick } from 'vue'

//pinia
import { createPinia } from 'pinia';
import { useFennecStore } from '@/domains/mode-fennec/store/fennecStore';
import { useFeatureFlagsStore } from '@/shared/store/featureFlagsStore';

import App from './App.vue'

// import './registerServiceWorker'
import router from './router'
import './plugins'
import i18n from './i18n'
import VueSweetalert2 from 'vue-sweetalert2'
import VueApexCharts from 'vue3-apexcharts'
import BootstrapVueNext from 'bootstrap-vue-next';
import 'sweetalert2/dist/sweetalert2.min.css'
import 'leaflet/dist/leaflet.css'
import 'swiper/css'
import 'glightbox/dist/css/glightbox.css'
import vClickOutside from 'click-outside-vue3';
import { initializePopupHelpers } from '@/shared/utils/popupHelper';
import { autoAnimatePlugin } from '@formkit/auto-animate/vue'
// Tailwind removed - using design-system.css instead
import { initialize as initializeTokenRefresh } from '@/shared/services/tokenRefreshService';
import { getAccessTokenToCookies, getAccessToken } from '@/shared/utils/tokenHelper';
import { userStore } from '@/shared/store/userStore';
import { guestDebug, isGuestPostPath } from '@/shared/config/guest.config';
import { hasValidAccessToken, isGuestSession } from '@/shared/utils/authSession.util';
import { installPassiveEventSupport } from '@/shared/utils/passiveEvents';
import { initWebSocketLifecycle } from '@/shared/services/webSocket';

// Install passive event listener support for better scroll performance
installPassiveEventSupport();

// Suppress Vue production warnings about key enumeration
// This is safe because we're not relying on key enumeration in production
// The warnings come from third-party libraries (FsLightbox) that we can't modify
if (process.env.NODE_ENV !== 'production') {
  const originalWarn = console.warn;
  console.warn = function (...args) {
    const msg = args[0];
    if (typeof msg === 'string' && msg.includes('Avoid app logic that relies on enumerating keys')) {
      // Skip this specific warning - it's from FsLightbox library
      return;
    }
    originalWarn.apply(console, args);
  };
}

const app = createApp(App)
const pinia = createPinia()

app.use(VueSweetalert2)
app.use(BootstrapVueNext)
app.use(VueApexCharts)
app.use(pinia)
initWebSocketLifecycle()
app.use(router)
app.use(i18n)
app.use(vClickOutside)
app.use(autoAnimatePlugin)

app.mount('#app')

// Initialize token refresh service and user data hydration after app mount
// Uses hydration pattern: instant data from cache, background refresh if stale
// Use nextTick to ensure router and stores are ready, but don't delay unnecessarily
nextTick(async () => {
  const fennecStore = useFennecStore();
  const featureFlagsStore = useFeatureFlagsStore();
  await router.isReady();
  featureFlagsStore.fetchFlags();
  fennecStore.handleRouteChange(router.currentRoute.value.name);

  const isUseCookies = process.env.VUE_APP_USE_COOKIES === 'true';
  const store = userStore();
  const onGuestPath = isGuestPostPath(globalThis.location?.pathname || '');

  guestDebug('main:bootstrap start', {
    isUseCookies,
    onGuestPath,
    hasToken: hasValidAccessToken(),
    userId: store.getUser()?.id,
    isUserFetched: store.isUserFetched,
  });

  if (isUseCookies) {
    // For cookie-based auth, httpOnly cookies cannot be read by JavaScript
    // So we can't check token existence - just try to fetch user data
    // The API will validate cookies and return user data if valid, or 401 if invalid
    initializeTokenRefresh();

    // Check if we have cached user data first (indicates previous valid session)
    if (store.isUserFetched && store.getUser().id) {
      // We have cached user data, so cookies are likely valid
      // Refresh in background if stale
      store.refreshIfStale();
    } else {
      // No cached data, try fetching (will validate cookies via API)
      // Don't await - let it fetch in background to avoid blocking app initialization
      store.fetchCurrentUser().catch((err) => {
        // If fetch fails (401), cookies are invalid - API interceptor will handle logout
        // Don't reset here - let the API interceptor handle it
        console.debug('Initial user fetch failed (cookies may be invalid):', err);
      });
    }
  } else {
    // For token-based auth, we can read the token from storage
    const token = getAccessToken();

    // Strict validation: reject "null", "undefined", or empty strings
    if (token && token !== 'null' && token !== 'undefined' && token.trim() !== '') {
      if (onGuestPath && isGuestSession()) {
        guestDebug('main:skip fetchCurrentUser on guest path (anonymous, stale token ignored)', {
          path: globalThis.location?.pathname,
        });
      } else {
      initializeTokenRefresh();

      // ✅ HYDRATION PATTERN: If user data was hydrated from cache, it's already available
      // Just refresh in background if stale, otherwise fetch immediately
      if (store.isUserFetched && store.getUser().id) {
        guestDebug('main:refreshIfStale (cached user)');
        // User data is already available from cache - refresh in background if stale
        store.refreshIfStale();
      } else {
        guestDebug('main:fetchCurrentUser on bootstrap');
        // No cache available, fetch immediately
        try {
          await store.fetchCurrentUser();
        } catch (err) {
          // Error fetching user on initialization
          guestDebug('main:fetchCurrentUser failed on bootstrap', { message: err?.message });
          console.debug('Initial user fetch failed:', err);
        }
      }
      }
    } else {
      guestDebug('main:no token — reset user store');
      // Reset user store if no token
      store.reset();
    }
  }
});

// Defer popup helpers initialization to avoid blocking initial render
setTimeout(() => {
  initializePopupHelpers()
}, 0)

// Global error handler for chunk loading errors and HTTP errors
window.addEventListener('unhandledrejection', (event) => {
  const error = event.reason;

  // Check for chunk loading errors
  const isChunkLoadError =
    error && (
      error.message && (
        error.message.includes('Loading chunk') ||
        error.message.includes('ChunkLoadError') ||
        error.message.includes('Failed to fetch dynamically imported module')
      ) ||
      error.name === 'ChunkLoadError'
    );

  if (isChunkLoadError) {
    // Clear all caches
    if ('caches' in window) {
      caches.keys().then(names => {
        names.forEach(name => {
          caches.delete(name);
        });
      });
    }
    // Prevent default error handling
    event.preventDefault();
    // Reload the page to get fresh chunks
    window.location.reload();
    return;
  }

  // Enhanced logging for HTTP/Axios errors
  if (error && (error.isAxiosError || error.response || error.request || error.config)) {
    // HTTP Request Error - handled silently
  } else if (error) {
    // Unhandled promise rejection - handled silently
  }
});

// Vue global error handler
app.config.errorHandler = (err, instance, info) => {
  // Vue Error Handler - handled silently
};

app.mixin({
  data() {
    return {
      breadcrumb: true
    }
  }
})


// Note: compilerOptions for custom elements (ion-*) is configured in vue.config.js
// This ensures it works with the runtime-only build of Vue.js


// Global Components
app.component('iq-card', require('@/shared/components/ui/Card/IqCard.vue').default)
app.component('loader', require('@/shared/components/feedback/Loader/Loader.vue').default)
app.component('tab-nav', require('@/shared/components/bootstrap/Tab/tab-nav.vue').default)
app.component('tab-nav-items', require('@/shared/components/bootstrap/Tab/tab-nav-items.vue').default)
app.component('tab-content', require('@/shared/components/bootstrap/Tab/tab-content.vue').default)
app.component('tab-content-item', require('@/shared/components/bootstrap/Tab/tab-content-item.vue').default)
app.component('TimeLine', require('@/shared/components/common/TimeLine.vue').default)
app.component('dropdown', require('@/shared/components/bootstrap/Dropdown/dropdown.vue').default)
app.component('dropdown-items', require('@/shared/components/bootstrap/Dropdown/dropdown-items.vue').default)
app.component('modal', require('@/shared/components/bootstrap/Modal/modal.vue').default)
app.component('model-header', require('@/shared/components/bootstrap/Modal/model-header.vue').default)
app.component('model-body', require('@/shared/components/bootstrap/Modal/model-body.vue').default)
app.component('model-footer', require('@/shared/components/bootstrap/Modal/model-footer.vue').default)
export { app }